import React from "react";
import { Box, Text, SimpleGrid, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";

// ==============================
// Custom Chakra-Compatible Icons
// ==============================

const PieChartIcon = (props) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21.21 15.89A10 10 0 1 1 8.12 2.79" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

const UsersIcon = (props) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M10 14a4 4 0 0 0-3 3.87V21" />
    <circle cx="10" cy="8" r="4" />
    <circle cx="17" cy="8" r="4" />
  </svg>
);

const FileTextIcon = (props) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

// ==============================
// Motion Animation Variants
// ==============================
const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.25,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const MotionBox = motion(Box);

// ==============================
// MAIN COMPONENT
// ==============================

const InsightsSection = () => {
  const cards = [
    {
      tag: "Know your market",
      icon: <PieChartIcon />,
      title: "Category Intelligence",
      text: "Understand where your market is heading, what's growing, and where opportunities exist all from public signals.",
    },
    {
      tag: "Watch competitors",
      icon: <UsersIcon />,
      title: "Competitive Intelligence",
      text: "Track competitor moves, positioning changes, and market signals without manual research or expensive services.",
    },
    {
      tag: "Act with confidence",
      icon: <FileTextIcon />,
      title: "Decision Summary",
      text: "Get plain language recommendations based on the data—not charts, but clear next steps you can act on today.",
    },
  ];

  return (
    <Box w="full" py="28" px="6" bg="#F7F9FC">

      {/* Heading */}
      <Box textAlign="center" maxW="3xl" mx="auto" mb="16">
        <Text
          fontSize="sm"
          letterSpacing="wide"
          color="#3B6A57"
          fontWeight="semibold"
          mb="3"
        >
          WHAT YOU GET
        </Text>

        <Text fontSize="5xl" fontWeight="bold" fontFamily="serif" color="#1A2C4E" mb="4">
          <Text as="span" color="#2AA198">Insights</Text>, not just information
        </Text>

        <Text fontSize="lg" color="gray.600">
          Three layers of intelligence that build on each other to guide your decisions.
        </Text>
      </Box>

      {/* Cards Grid */}
      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        spacing="10"
        maxW="7xl"
        mx="auto"
      >
        {cards.map((card, i) => (
          <MotionBox
            key={i}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={cardVariants}
            bg="white"
            borderRadius="3xl"
            boxShadow="0px 8px 20px rgba(0,0,0,0.05)"
            border="1px solid #EDF0F4"
            p="10"
            position="relative"
            h="full"
          >
            {/* Tag Badge */}
            <Flex
              align="center"
              gap="2"
              position="absolute"
              top="8"
              left="8"
              fontSize="md"
              fontWeight="medium"
              color="#3B6A57"
            >
              <Text px="4" py="1" borderRadius="full" bg="transparent">
                • {card.tag}
              </Text>
            </Flex>

            {/* Icon */}
            <Flex
              w="16"
              h="16"
              bg="#E8EFF7"
              borderRadius="2xl"
              mb="6"
              mt="16"
              align="center"
              justify="center"
            >
              {card.icon}
            </Flex>

            {/* Title */}
            <Text
              fontSize="2xl"
              fontWeight="bold"
              fontFamily="serif"
              color="#1A2C4E"
              mb="4"
            >
              {card.title}
            </Text>

            {/* Description */}
            <Text fontSize="17px" color="gray.600" lineHeight="1.7">
              {card.text}
            </Text>
          </MotionBox>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default InsightsSection;